import { useState, useContext, useEffect } from 'react'
import { PinInput } from 'react-input-pin-code';
import { useNavigate } from 'react-router-dom'
import Countdown, { CountdownRenderProps } from 'react-countdown';
import { Box, Button, Grid, Stack, Typography } from '@mui/material'
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import { AuthContext } from '../../contexts/auth/authContext'
import AuthHeader from '../../components/auth/authHeader';
import AuthError from '../../components/auth/authError'
import AuthSuccess from '../../components/auth/authSuccess'
import custom from '../../styles/mui/szVariables.json'
import { analyticsPage, analyticsDomain, analyticsCta } from '../../services/constants'
import { sendClickAnalytics, sendViewAnalytics } from '../../components/common/util/util'

export default function VerifyCode() {
  const navigate = useNavigate()
  const auth = useContext(AuthContext)   
  const [ error, setError ] = useState('')
  const [ codes, setCodes ] = useState(['', '', '','', '', '']);
  const [ message, setMessage ] = useState({
    title: "Verification code sent",
    message: `We’ve sent a verification code to your email ${auth.userEmail}`
  } as any)

  useEffect(() => {
    sendViewAnalytics('/verifycode', {
        domain: analyticsDomain.welcome,
        name: analyticsPage.verifyCode
      })
    if(!auth.otp?.expiration)  navigate('/requestcode')
  },[auth, navigate])
  
  const handlePinInput = (
      value: string | string[], 
      index: number, 
      values: string[]
    ) => {
      setCodes(values)
  }

  const getAuthCode = async () => {
    try {
      setError('')
      sendClickAnalytics('/verifycode', {
          domain: analyticsDomain.welcome,
          name: analyticsCta.emailValidationVerifyCode,
          metadata: {},
      })
      if(codes.every(c=>c)) {
        const code = codes.join('');
        await auth.getAuthCode(code)
        navigate('/passwordchange')
      } else {
        navigate('/requestcode')
      }
    } catch (err: any) {
      setError(err.message)
      setMessage(null)
    }
  }

  function handleEnterKey(ev: any) {
    if(ev.key === "Enter" && codes.every(c=>c)) {
      getAuthCode()
    }
  }


  const resendCodeClicked = async () => {
    navigate('/requestcode')
  }

  const renderer: any = (
    { minutes, seconds, completed }: CountdownRenderProps) => {
    if (completed) {
      return  navigate('/requestcode')
    } else {
      return (
        <Stack alignItems="center" direction="row" gap={1} pt={1}>
          <AccessTimeIcon sx={{ color:"text.secondary" }}/>
          <Typography variant="szBody.Regular.Small"
            color="text.secondary">
            {minutes}:{seconds<10?`0${seconds}`:seconds}
          </Typography>
        </Stack>
      );
    }
  };

  return (
    <AuthHeader 
      id="verifycode" 
      title="Enter the verification code"
      description="If your email address is associated with a SolarZero account, you should receive a verification code shortly.  Enter the code below:"
      >

        <AuthError error={error} />
        <AuthSuccess message={message} />

        <Box width="100%" mt={custom.spacing.sm}>
          <Grid container 
              direction="column" 
              justifyContent="center" 
              alignContent="center" >
            <Grid item xs={12} width="100%" 
              justifyContent="center"
              display="flex">
                <PinInput
                  size='lg'
                  id="codeInput"
                  autoFocus={true}
                  values={codes}
                  onKeyDown={handleEnterKey}
                  onChange={handlePinInput}
                  placeholder=''
                />
            </Grid>
            <Grid item xs={12} 
              mt={custom.spacing.md}
              justifyContent={"center"}
             >
              <Stack alignItems="center" 
                  direction="row" 
                  justifyContent={"space-between"}
                  px={"60px"}>
                  <Countdown
                    date={auth?.otp?.expiration || 
                            (Date.now() + (1000*60*10))}
                    renderer={renderer}
                  />
                  <Box id="resendCodeLink" 
                      onClick={resendCodeClicked} 
                      sx={{cursor:"pointer"}}>
                    <Typography variant="szBody.Regular.Small" 
                      color="primary" >
                      Resend the code
                    </Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} mt={custom.spacing.md} width="100%">
              <Button id="signIn" fullWidth={true}  
                  disabled={codes.some(c=>!c)} 
                  color="primary" variant="contained" 
                  onKeyUp={handleEnterKey} 
                  onClick={getAuthCode}>
                Verify
              </Button>
            </Grid>
          </Grid>
        </Box>
    </AuthHeader>
  )
}

